import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { School, CheckCircle, AlertCircle, Loader2, ChevronDown, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import PasswordStrengthMeter from '../../components/PasswordStrengthMeter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SchoolRegistration = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [refData, setRefData] = useState({ states: [], grades: [], school_types: [] });
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    school_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    school_district: '',
    school_type: '',
    student_count_estimate: '',
    grade_levels: [],
    contact_name: '',
    contact_title: '',
    admin_email: '',
    admin_password: '',
    confirm_password: '',
    phone: '',
    has_parental_consent_framework: false,
    has_read_safeguarding_policy: false,
    referral_source: '',
  });

  useEffect(() => {
    fetch(`${API_URL}/api/school/reference-data`)
      .then(r => r.json())
      .then(d => setRefData(d))
      .catch(() => {});
  }, []);

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const toggleGrade = (g) => {
    setForm(p => ({
      ...p,
      grade_levels: p.grade_levels.includes(g)
        ? p.grade_levels.filter(x => x !== g)
        : [...p.grade_levels, g],
    }));
  };

  const validateStep1 = () => {
    if (!form.school_name.trim()) return 'School name is required';
    if (!form.address_line1.trim()) return 'Address is required';
    if (!form.city.trim()) return 'City is required';
    if (!form.state) return 'State is required';
    if (!form.zip_code.trim()) return 'ZIP code is required';
    if (!form.school_district.trim()) return 'School district is required';
    if (!form.school_type) return 'School type is required';
    if (form.grade_levels.length === 0) return 'Select at least one grade level';
    return null;
  };

  const validateStep2 = () => {
    if (!form.contact_name.trim()) return 'Contact name is required';
    if (!form.contact_title) return 'Contact title is required';
    if (!form.admin_email.trim()) return 'Work email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.admin_email)) return 'Enter a valid email address';
    if (!form.admin_password || form.admin_password.length < 8) return 'Password must be at least 8 characters';
    if (form.admin_password !== form.confirm_password) return 'Passwords do not match';
    if (!form.has_parental_consent_framework) return 'You must confirm parental consent frameworks are in place';
    if (!form.has_read_safeguarding_policy) return 'You must confirm you have read the HiBFF Safeguarding Policy';
    return null;
  };

  const handleNext = () => {
    const err = validateStep1();
    if (err) { setError(err); return; }
    setError('');
    setStep(2);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validateStep2();
    if (err) { setError(err); return; }
    setError('');
    setLoading(true);

    try {
      const body = {
        ...form,
        student_count_estimate: parseInt(form.student_count_estimate) || 0,
      };
      delete body.confirm_password;

      const res = await fetch(`${API_URL}/api/school/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Registration failed. Please try again.');
      } else {
        setSubmitted(true);
      }
    } catch {
      setError('Network error. Please try again.');
    }
    setLoading(false);
  };

  const TITLES = ['Principal', 'Vice Principal', 'Pastoral Lead', 'Counselor', 'Head of Wellbeing', 'IT Administrator', 'Other'];

  const REFERRAL_OPTIONS = ['Web search', 'Social media', 'Conference or event', 'Colleague recommendation', 'HiBFF team outreach', 'News article', 'Other'];

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F8F7F4' }}>
        <div className="max-w-lg w-full text-center">
          <div className="bg-white rounded-2xl p-10" style={{ border: '1px solid #E8E8E8' }}>
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: '#2EAA5C15' }}>
              <CheckCircle size={32} style={{ color: '#2EAA5C' }} />
            </div>
            <h1 className="text-2xl font-bold mb-3" style={{ color: '#1A1209' }}>Application Received</h1>
            <p className="text-sm leading-relaxed mb-6" style={{ color: '#666' }} data-testid="success-message">
              Thank you — your application has been received. A member of the HiBFF team will review it within 48 hours and contact you at the email address you provided.
            </p>
            <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left" style={{ backgroundColor: '#F8F7F4' }}>
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs" style={{ color: '#999' }}>School</span>
                <span className="text-sm font-semibold" style={{ color: '#1A1209' }}>{form.school_name}</span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs" style={{ color: '#999' }}>Contact</span>
                <span className="text-sm" style={{ color: '#333' }}>{form.contact_name}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs" style={{ color: '#999' }}>Email</span>
                <span className="text-sm" style={{ color: '#333' }}>{form.admin_email}</span>
              </div>
            </div>
            <p className="text-xs mb-6" style={{ color: '#AAA' }}>
              You can log in at any time to check your application status.
            </p>
            <div className="flex gap-3 justify-center">
              <Link to="/login" className="px-5 py-2.5 rounded-lg text-sm font-semibold text-white" style={{ backgroundColor: '#F5831F' }} data-testid="go-to-login">
                Go to Login
              </Link>
              <Link to="/" className="px-5 py-2.5 rounded-lg text-sm font-medium" style={{ backgroundColor: '#F0F0F0', color: '#666' }}>
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8F7F4' }}>
      {/* Header */}
      <header className="bg-white" style={{ borderBottom: '1px solid #E8E8E8' }}>
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
            <img src="/hibff-logo.png" alt="HiBFF" className="h-10" />
          </Link>
          <Link to="/login" className="text-sm font-medium" style={{ color: '#F5831F' }} data-testid="login-link">
            Already registered? Log in
          </Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Title */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2.5 mb-3">
            <School size={24} style={{ color: '#7B4FD8' }} />
            <h1 className="text-2xl font-bold" style={{ color: '#1A1209' }}>HiBFF Schools — 14-Day Free Trial</h1>
          </div>
          <p className="text-sm max-w-md mx-auto" style={{ color: '#888' }}>
            Help your students build social confidence with AvA — a private practice partner for real-world social skills. No therapy. No tutoring. Just practice.
          </p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{ backgroundColor: '#F5831F' }}>1</div>
            <span className="text-sm font-medium" style={{ color: step === 1 ? '#1A1209' : '#AAA' }}>School Details</span>
          </div>
          <div className="w-8 h-px" style={{ backgroundColor: '#DDD' }} />
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" style={step === 2 ? { backgroundColor: '#F5831F', color: 'white' } : { backgroundColor: '#E8E8E8', color: '#AAA' }}>2</div>
            <span className="text-sm font-medium" style={{ color: step === 2 ? '#1A1209' : '#AAA' }}>Contact & Account</span>
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2.5 px-4 py-3 rounded-lg mb-5" style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA' }} data-testid="form-error">
            <AlertCircle size={16} style={{ color: '#DC2626', flexShrink: 0, marginTop: 2 }} />
            <p className="text-sm" style={{ color: '#DC2626' }}>{error}</p>
          </div>
        )}

        <form onSubmit={step === 2 ? handleSubmit : (e) => { e.preventDefault(); handleNext(); }}>
          <div className="bg-white rounded-2xl p-8" style={{ border: '1px solid #E8E8E8' }}>
            {/* STEP 1: School Details */}
            {step === 1 && (
              <div data-testid="step-1">
                <h2 className="text-lg font-bold mb-5" style={{ color: '#1A1209' }}>School Information</h2>

                <div className="space-y-4">
                  <Field label="School Name" required>
                    <input value={form.school_name} onChange={e => set('school_name', e.target.value)} placeholder="e.g. Lincoln Middle School" className="form-input" data-testid="school-name" />
                  </Field>

                  <Field label="Street Address" required>
                    <input value={form.address_line1} onChange={e => set('address_line1', e.target.value)} placeholder="Street address" className="form-input" data-testid="address-line1" />
                    <input value={form.address_line2} onChange={e => set('address_line2', e.target.value)} placeholder="Suite, building, floor (optional)" className="form-input mt-2" data-testid="address-line2" />
                  </Field>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Field label="City" required>
                      <input value={form.city} onChange={e => set('city', e.target.value)} placeholder="City" className="form-input" data-testid="city" />
                    </Field>
                    <Field label="State" required>
                      <div className="relative">
                        <select value={form.state} onChange={e => set('state', e.target.value)} className="form-input appearance-none pr-8" data-testid="state">
                          <option value="">Select state</option>
                          {refData.states.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#AAA' }} />
                      </div>
                    </Field>
                    <Field label="ZIP Code" required>
                      <input value={form.zip_code} onChange={e => set('zip_code', e.target.value)} placeholder="ZIP" className="form-input" data-testid="zip-code" />
                    </Field>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Field label="School District / Trust / Local Authority" required>
                      <input value={form.school_district} onChange={e => set('school_district', e.target.value)} placeholder="e.g. Austin ISD, Harris Academy Trust" className="form-input" data-testid="school-district" />
                    </Field>
                    <Field label="School Type" required>
                      <div className="relative">
                        <select value={form.school_type} onChange={e => set('school_type', e.target.value)} className="form-input appearance-none pr-8" data-testid="school-type">
                          <option value="">Select type</option>
                          {refData.school_types.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#AAA' }} />
                      </div>
                    </Field>
                  </div>

                  <Field label="Total Student Enrollment (approximate)">
                    <input type="number" value={form.student_count_estimate} onChange={e => set('student_count_estimate', e.target.value)} placeholder="e.g. 800" className="form-input" data-testid="student-count" />
                  </Field>

                  <Field label="Grade Levels Served" required>
                    <div className="flex flex-wrap gap-2" data-testid="grade-levels">
                      {(refData.grades.length > 0 ? refData.grades : ['6th','7th','8th','9th','10th','11th','12th']).map(g => (
                        <button type="button" key={g} onClick={() => toggleGrade(g)}
                          className="px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                          style={form.grade_levels.includes(g) ? { backgroundColor: '#7B4FD8', color: 'white' } : { backgroundColor: '#F0F0F0', color: '#666' }}
                          data-testid={`grade-${g}`}>
                          Grade {g}
                        </button>
                      ))}
                    </div>
                  </Field>
                </div>

                <div className="flex justify-end mt-8">
                  <button type="submit" className="px-6 py-2.5 rounded-lg text-sm font-semibold text-white" style={{ backgroundColor: '#F5831F' }} data-testid="next-step-btn">
                    Continue to Contact Details
                  </button>
                </div>
              </div>
            )}

            {/* STEP 2: Contact & Account */}
            {step === 2 && (
              <div data-testid="step-2">
                <button type="button" onClick={() => { setStep(1); setError(''); }} className="flex items-center gap-1 text-sm mb-4" style={{ color: '#F5831F' }} data-testid="back-btn">
                  <ArrowLeft size={14} /> Back to School Details
                </button>

                <h2 className="text-lg font-bold mb-5" style={{ color: '#1A1209' }}>Primary Contact & Account Setup</h2>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Full Name" required>
                      <input value={form.contact_name} onChange={e => set('contact_name', e.target.value)} placeholder="e.g. Dr. Sarah Johnson" className="form-input" data-testid="contact-name" />
                    </Field>
                    <Field label="Title / Role" required>
                      <div className="relative">
                        <select value={form.contact_title} onChange={e => set('contact_title', e.target.value)} className="form-input appearance-none pr-8" data-testid="contact-title">
                          <option value="">Select title</option>
                          {TITLES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#AAA' }} />
                      </div>
                    </Field>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Work Email" required>
                      <input type="email" value={form.admin_email} onChange={e => set('admin_email', e.target.value)} placeholder="you@school.edu" className="form-input" data-testid="admin-email" />
                    </Field>
                    <Field label="Phone Number">
                      <input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(555) 123-4567" className="form-input" data-testid="phone" />
                    </Field>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Create Password" required>
                      <div className="relative">
                        <input type={showPassword ? 'text' : 'password'} value={form.admin_password} onChange={e => set('admin_password', e.target.value)} placeholder="Minimum 8 characters" className="form-input pr-10" data-testid="admin-password" />
                        <button type="button" onClick={() => setShowPassword(s => !s)} aria-label={showPassword ? 'Hide password' : 'Show password'} data-testid="admin-password-toggle-btn" tabIndex={-1} className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600">
                          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                      <PasswordStrengthMeter password={form.admin_password} testIdPrefix="school-pwd-strength" />
                    </Field>
                    <Field label="Confirm Password" required>
                      <div className="relative">
                        <input type={showConfirmPassword ? 'text' : 'password'} value={form.confirm_password} onChange={e => set('confirm_password', e.target.value)} placeholder="Confirm password" className="form-input pr-10" data-testid="confirm-password" />
                        <button type="button" onClick={() => setShowConfirmPassword(s => !s)} aria-label={showConfirmPassword ? 'Hide password' : 'Show password'} data-testid="confirm-password-toggle-btn" tabIndex={-1} className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600">
                          {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </Field>
                  </div>

                  <Field label="How did you hear about HiBFF?">
                    <div className="relative">
                      <select value={form.referral_source} onChange={e => set('referral_source', e.target.value)} className="form-input appearance-none pr-8" data-testid="referral-source">
                        <option value="">Select (optional)</option>
                        {REFERRAL_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                      <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#AAA' }} />
                    </div>
                  </Field>

                  {/* Compliance checkboxes */}
                  <div className="pt-4" style={{ borderTop: '1px solid #F0F0F0' }}>
                    <label className="flex items-start gap-3 cursor-pointer mb-3" data-testid="consent-checkbox">
                      <input type="checkbox" checked={form.has_parental_consent_framework} onChange={e => set('has_parental_consent_framework', e.target.checked)}
                        className="w-4 h-4 mt-0.5 rounded" style={{ accentColor: '#7B4FD8' }} />
                      <span className="text-sm" style={{ color: '#555' }}>
                        I confirm that our school has parental consent frameworks in place for student participation in wellbeing programs. <span style={{ color: '#DC2626' }}>*</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer" data-testid="safeguarding-checkbox">
                      <input type="checkbox" checked={form.has_read_safeguarding_policy} onChange={e => set('has_read_safeguarding_policy', e.target.checked)}
                        className="w-4 h-4 mt-0.5 rounded" style={{ accentColor: '#7B4FD8' }} />
                      <span className="text-sm" style={{ color: '#555' }}>
                        I confirm that I have read the <a href="/terms" target="_blank" rel="noopener noreferrer" className="font-medium underline" style={{ color: '#7B4FD8' }}>HiBFF Safeguarding Policy</a>. <span style={{ color: '#DC2626' }}>*</span>
                      </span>
                    </label>
                  </div>
                </div>

                <div className="flex justify-end mt-8">
                  <button type="submit" disabled={loading}
                    className="px-7 py-2.5 rounded-lg text-sm font-semibold text-white flex items-center gap-2 disabled:opacity-50"
                    style={{ backgroundColor: '#7B4FD8' }} data-testid="submit-btn">
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <School size={16} />}
                    {loading ? 'Submitting...' : 'Submit Application'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </form>

        {/* Privacy note */}
        <p className="text-center text-xs mt-6" style={{ color: '#BBB' }}>
          HiBFF is COPPA compliant. Student conversations are always private — schools receive aggregated, anonymised data only.
        </p>
      </div>

      <style>{`
        .form-input {
          width: 100%;
          padding: 10px 14px;
          border: 1px solid #E0E0E0;
          border-radius: 10px;
          font-size: 14px;
          color: #1A1209;
          background: white;
          outline: none;
          transition: border-color 0.2s;
        }
        .form-input:focus {
          border-color: #7B4FD8;
          box-shadow: 0 0 0 3px rgba(123,79,216,0.08);
        }
        .form-input::placeholder {
          color: #C0C0C0;
        }
      `}</style>
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-medium mb-1.5" style={{ color: '#555' }}>
      {label} {required && <span style={{ color: '#DC2626' }}>*</span>}
    </label>
    {children}
  </div>
);

export default SchoolRegistration;
